import { ArrowRight } from 'lucide-react';

import type { FlowStep } from './volumetricAnalysis';

const BURETTE_CAPACITY_ML = 50;
const BURETTE_TOP = 16;
const BURETTE_BOTTOM = 236;
const BURETTE_X = 22;
const BURETTE_WIDTH = 22;
const MAJOR_TICK_ML = 5;

/**
 * Schematic 50 mL burette. The scale reads downwards from 0.00 mL at the top, so the liquid level
 * after a run sits at the titre (start reading 0.00 mL). Geometry only; no apparatus artwork.
 */
export function BuretteSchematic({ readingMl, runLabel }: { readingMl: number; runLabel: string }) {
  const clamped = Math.min(Math.max(readingMl, 0), BURETTE_CAPACITY_ML);
  const yFor = (ml: number) =>
    BURETTE_TOP + (ml / BURETTE_CAPACITY_ML) * (BURETTE_BOTTOM - BURETTE_TOP);
  const level = yFor(clamped);
  const ticks = Array.from(
    { length: BURETTE_CAPACITY_ML / MAJOR_TICK_ML + 1 },
    (_, index) => index * MAJOR_TICK_ML,
  );
  return (
    <figure className="flex items-start gap-3">
      <svg
        viewBox="0 0 96 260"
        className="h-64 w-24 shrink-0"
        role="img"
        aria-label={`Burette schematic for ${runLabel}: start reading 0.00 mL, final reading ${clamped.toFixed(2)} mL, so ${clamped.toFixed(2)} mL was delivered.`}
      >
        <rect
          x={BURETTE_X}
          y={BURETTE_TOP}
          width={BURETTE_WIDTH}
          height={BURETTE_BOTTOM - BURETTE_TOP}
          rx={4}
          className="fill-wash-sky stroke-slate2"
          strokeWidth={1.5}
        />
        <rect
          x={BURETTE_X + 1.5}
          y={level}
          width={BURETTE_WIDTH - 3}
          height={BURETTE_BOTTOM - level}
          className="fill-brand-sky/60"
        />
        <line
          x1={BURETTE_X - 6}
          x2={BURETTE_X + BURETTE_WIDTH + 6}
          y1={level}
          y2={level}
          className="stroke-ink"
          strokeWidth={2}
          strokeDasharray="4 2"
        />
        {ticks.map((ml) => (
          <g key={ml}>
            <line
              x1={BURETTE_X + BURETTE_WIDTH}
              x2={BURETTE_X + BURETTE_WIDTH + 6}
              y1={yFor(ml)}
              y2={yFor(ml)}
              className="stroke-slate2"
            />
            <text
              x={BURETTE_X + BURETTE_WIDTH + 9}
              y={yFor(ml) + 3}
              className="fill-slate2 text-[9px] font-bold"
            >
              {ml}
            </text>
          </g>
        ))}
        <path
          d={`M${BURETTE_X + 7} ${BURETTE_BOTTOM} L${BURETTE_X + BURETTE_WIDTH - 7} ${BURETTE_BOTTOM} L${BURETTE_X + BURETTE_WIDTH / 2 + 1} ${BURETTE_BOTTOM + 18} L${BURETTE_X + BURETTE_WIDTH / 2 - 1} ${BURETTE_BOTTOM + 18} Z`}
          className="fill-stone2"
        />
      </svg>
      <figcaption className="text-xs font-bold leading-5 text-slate2">
        <span className="block text-sm font-extrabold text-ink">{runLabel}</span>
        Start 0.00 mL
        <br />
        Final {clamped.toFixed(2)} mL
        <br />
        Titre {clamped.toFixed(2)} mL
        <br />
        Read the bottom of the meniscus at eye level.
      </figcaption>
    </figure>
  );
}

export interface TitreRow {
  label: string;
  value: number;
  rough: boolean;
  concordant: boolean;
}

const AXIS_PADDING_ML = 0.3;

/** Titre bars on a zoomed axis so a 0.10 mL difference is visible; concordant runs carry a tick. */
export function TitreChart({ rows, revealed }: { rows: TitreRow[]; revealed: boolean }) {
  const values = rows.map((row) => row.value);
  const minimum = Math.floor((Math.min(...values) - AXIS_PADDING_ML) * 10) / 10;
  const maximum = Math.ceil((Math.max(...values) + AXIS_PADDING_ML) * 10) / 10;
  const widthFor = (value: number) => ((value - minimum) / (maximum - minimum)) * 100;
  return (
    <figure>
      <ol className="space-y-1.5" aria-label="Titre bar chart">
        {rows.map((row) => (
          <li
            key={row.label}
            className="grid grid-cols-[4.5rem_minmax(0,1fr)_3.5rem] items-center gap-2"
          >
            <span className="text-xs font-extrabold text-ink">{row.label}</span>
            <span className="h-4 rounded-full bg-surface">
              <span
                className={`block h-4 rounded-full ${
                  row.rough
                    ? 'bg-stone2'
                    : revealed && row.concordant
                      ? 'bg-brand-mint'
                      : 'bg-brand-sky/60'
                }`}
                style={{ width: `${widthFor(row.value)}%` }}
              />
            </span>
            <span className="text-right text-xs font-bold tabular-nums text-slate2">
              {row.value.toFixed(2)}
              {revealed && row.concordant ? ' ✓' : ''}
            </span>
          </li>
        ))}
      </ol>
      <figcaption className="mt-2 text-xs font-medium text-slate2">
        Axis starts at {minimum.toFixed(1)} mL so small differences show. Grey = rough titre.
        {revealed ? ' Green with ✓ = concordant (within 0.10 mL).' : ''}
      </figcaption>
    </figure>
  );
}

/** Mole map: each box is one calculated quantity, in order. */
export function MoleMap({ steps }: { steps: FlowStep[] }) {
  return (
    <ol
      className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-stretch"
      aria-label="Mole map"
    >
      {steps.map((step, index) => (
        <li key={`${step.label}-${index}`} className="flex items-center gap-2">
          <span className="min-w-0 rounded-2xl border border-hairline bg-white px-3 py-2">
            <span className="block text-[0.7rem] font-extrabold uppercase tracking-wide text-slate2">
              {step.label}
            </span>
            <span className="block break-words text-sm font-extrabold text-ink">{step.value}</span>
          </span>
          {index < steps.length - 1 ? (
            <ArrowRight
              className="hidden h-4 w-4 shrink-0 text-slate2 sm:block"
              aria-hidden="true"
            />
          ) : null}
        </li>
      ))}
    </ol>
  );
}
